<template>
  <div ref="host" class="arbitrium-react-island"></div>
</template>

<script>
import React from "react";
import { createRoot } from "react-dom/client";

export default {
  name: "ArbitriumReactIsland",

  props: {
    identificationId: { type: String, default: "" },
    personName: { type: String, default: "" },
    personDateOfBirth: { type: String, default: "" },
    personGender: { type: String, default: "" },
    personCountry: { type: String, default: "" },
    // A function, not a snapshot: the underlying token can refresh (see
    // src/utils/api.js), so React must always read the current value.
    getAuthToken: { type: Function, default: () => "" },
    // Passed as a prop, not read via process.env inside @arbitrium/react: the
    // lib is pre-built by Vite before this app's webpack build runs, so a
    // process.env reference there wouldn't get replaced.
    apiBaseUrl: {
      type: String,
      default: () => process.env.VUE_APP_ARBITRIUM_API
    },
    // BCP-47 language tag. @arbitrium/react owns the AML-screening strings and
    // looks them up for this locale (see the lib's own i18n); Vue stays the
    // source of truth for which language is shown.
    locale: { type: String, default: "en" },
    open: { type: Boolean, default: true }
  },

  computed: {
    // One object so a single watcher re-renders React on any prop change.
    // onClose/onStatusChange bridge React's callback props to Vue $emit so this
    // component's public API stays idiomatic Vue (@close/@status-change).
    reactProps() {
      return {
        identificationId: this.identificationId,
        personName: this.personName,
        personDateOfBirth: this.personDateOfBirth,
        personGender: this.personGender,
        personCountry: this.personCountry,
        getAuthToken: this.getAuthToken,
        apiBaseUrl: this.apiBaseUrl,
        locale: this.locale,
        open: this.open,
        onClose: () => this.$emit("close"),
        onStatusChange: change => this.$emit("status-change", change)
      };
    }
  },

  watch: {
    reactProps() {
      this.renderReact();
    }
  },

  mounted() {
    this.shadow = this.$refs.host.attachShadow({ mode: "open" });

    const reset = document.createElement("style");
    reset.textContent = `
      :host {
        all: initial;
        display: block;
        height: 100%;
        font-family: Arial, Helvetica, sans-serif;
      }

      *, *::before, *::after {
        box-sizing: border-box;
      }
    `;
    this.shadow.appendChild(reset);

    this.mountEl = document.createElement("div");
    this.mountEl.style.height = "100%";
    this.shadow.appendChild(this.mountEl);

    // Load the React entry lazily. In prod this pulls the self-contained dist
    // bundle; in dev it pulls the TS source so Fast Refresh can hot-reload it.
    import("@arbitrium/react")
      .then(({ AmlScreeningRoot }) => {
        // The component may have been destroyed while this import was in
        // flight. Bail so we don't mount React into a detached node (and kick
        // off the AML fetch) on a dead component.
        if (this._destroyed) return;

        this.mirrorLibStyles();

        this.AmlScreeningRoot = AmlScreeningRoot;
        this.reactRoot = createRoot(this.mountEl);
        this.renderReact();
      })
      .catch(() => {
        if (this._destroyed) return;
        this.mountEl.textContent =
          "Failed to load AML screening. Please reload the page.";
      });
  },

  methods: {
    // @arbitrium/react injects its CSS into document.head (#arbitrium-react-styles
    // in prod, [data-arbitrium-react-style] in dev), which can't cross the shadow
    // boundary. Clone (not move) those <style>s in, so each fresh shadow root on
    // re-navigation gets styled regardless of mount/unmount order.
    mirrorLibStyles() {
      const sources = document.querySelectorAll(
        "#arbitrium-react-styles, [data-arbitrium-react-style]"
      );
      this.styleObservers = [];

      sources.forEach(source => {
        const clone = document.createElement("style");
        clone.textContent = source.textContent;
        this.shadow.appendChild(clone);

        // Dev only: mirror style-loader's in-place hot-updates into the clone.
        // In prod the source never mutates, so this observer never fires.
        const observer = new MutationObserver(() => {
          clone.textContent = source.textContent;
        });
        observer.observe(source, {
          childList: true,
          characterData: true,
          subtree: true
        });
        this.styleObservers.push(observer);
      });
    },

    renderReact() {
      if (!this.reactRoot || !this.AmlScreeningRoot) return;

      this.reactRoot.render(
        React.createElement(this.AmlScreeningRoot, this.reactProps)
      );
    }
  },

  beforeDestroy() {
    // Guards the async import().then() in mounted(): if we're torn down before
    // it resolves, it must not mount React after the fact.
    this._destroyed = true;
    if (this.reactRoot) {
      this.reactRoot.unmount();
    }
    if (this.styleObservers) {
      this.styleObservers.forEach(observer => observer.disconnect());
    }
  }
};
</script>

<style scoped>
.arbitrium-react-island {
  display: block;
  height: 100%;
}
</style>
